import React from 'react';
import { Space, Typography, Button, Dropdown } from 'antd';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { Cable } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { customStyles, getThemeStyles } from '../theme';
import { useThemeStyles, useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const Navbar = ({ showAuth = false, forcePublicDark = false }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const ctxStyles = useThemeStyles();
  const themeStyles = forcePublicDark ? getThemeStyles('dark') : ctxStyles;
  const isLight = !forcePublicDark && theme === 'light';
  const { user, isAuthenticated, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/splash');
  };

  const userMenuItems = [
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '0 16px 0 16px', 
      background: themeStyles.navbarBg, 
      height: '48px', 
      boxShadow: isLight ? 'none' : `0 1px 0 ${themeStyles.borderColor}`,
      borderBottom: isLight ? `1px solid ${themeStyles.shellDivider}` : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, minWidth: 0 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <svg width="0" height="0" style={{ position: 'absolute', left: 0, top: 0 }} aria-hidden>
            <defs>
              <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F62E97" />
                <stop offset="100%" stopColor="#153CB4" />
              </linearGradient>
            </defs>
          </svg>
          <Cable size={22} strokeWidth={1.5} stroke="url(#iconGradient)" />
        </span>
        <Title level={3} style={{ 
          margin: 0, 
          lineHeight: '1', 
          ...customStyles.brandGradientText, 
          fontWeight: 700, 
          letterSpacing: '-0.3px',
          fontSize: '18px',  
          textShadow: 'none'
        }}>Enterprise AI Platform</Title>
      </div>
      <Space size="middle" align="center">
        {!forcePublicDark ? (
          <ThemeToggle size="small" type="text" style={{ color: themeStyles.textSecondary }} />
        ) : null}
        {showAuth ? (
          <>
            <Button type="text" style={{ color: themeStyles.textPrimary }} onClick={() => navigate('/login')}>Login</Button>
            <Button type="primary" style={{ ...customStyles.primaryButton }} onClick={() => navigate('/register')}>Register</Button>
          </>
        ) : isAuthenticated ? (
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.currentTarget.click();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 28,
                boxSizing: 'border-box',
                padding: '0 10px',
                borderRadius: 8,
                cursor: 'pointer',
                maxWidth: 240,
                background: isLight ? '#F3F4F6' : 'rgba(255, 255, 255, 0.06)',
                border: isLight ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <UserOutlined style={{ color: themeStyles.textPrimary, fontSize: 14, flexShrink: 0 }} />
              <Text
                style={{
                  color: themeStyles.textPrimary,
                  fontSize: 12,
                  margin: 0,
                  lineHeight: 1,
                }}
                ellipsis
              >
                {user?.username}
              </Text>
            </div>
          </Dropdown>
        ) : (
          <>
            <Button type="text" style={{ color: themeStyles.textPrimary }} onClick={() => navigate('/login')}>Login</Button>
            <Button type="primary" style={{ ...customStyles.primaryButton }} onClick={() => navigate('/register')}>Register</Button>
          </>
        )}
      </Space>
    </div>
  );
};

export default Navbar;
